import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Toggle } from "react-toggle-component";
import moment from "moment";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

import CancelButton from "src/Components/Common/cancelbutton";
import UserService from "src/Services/API/user.service";
import UserFormPermissionService from "src/Services/API/userformpermission.service";
import RoleService from "src/Services/API/role.service";
import BranchService from "src/Services/API/branch.service";
import ConfirmDialog from "src/Common/confirmdialog";
import { getImageUrl } from "src/Common/common";
import InviteModal from "../Invite/InviteModal/invitemodal";
import InviteService from "src/Services/API/invite.service";
import RoleDropDown from "./roledropdown";
import BranchDropDown from "./branchdropdown";
import { setBranch } from "src/Redux/Slices/BranchSlice";
import { handleError, isSuperAdmin, formatUSPhone, isValidUSPhone, toE164Phone, isAdmin } from "src/Common/function";

function Manage({ branch }) {
  const navigate = useNavigate();
  const currentUser = UserService.getCurrentUser();
  const superAdmin = isSuperAdmin();
  const [userArray, setUserArray] = useState([]);
  const [index, setIndex] = useState(0);
  const [isDeleteConfirmDialogShown, setIsDeleteConfirmDialogShown] = useState(false);
  const [roleChangedUserArray, setRoleChangedUserArray] = useState([]);
  const [editingPhoneUserId, setEditingPhoneUserId] = useState(null);
  const [editingPhoneValue, setEditingPhoneValue] = useState("");
  const [formEnabledMap, setFormEnabledMap] = useState({});
  const [roleArray, setRoleArray] = useState([]);
  const [branchArray, setBranchArray] = useState([]);

  useEffect(() => {
    console.log("🚀 ~ Manage ~ branch:", branch);
    getAllUsers();
  }, [branch]);

  useEffect(() => {
    RoleService.getAll()
      .then((response) => {
        if (response.status == 200) setRoleArray(response.data);
      })
      .catch((error) => handleError(error));

    BranchService.getAll()
      .then((response) => {
        if (response.status == 200) setBranchArray(response.data);
      })
      .catch((error) => handleError(error));
  }, []);

  const getAllUsers = () => {
    UserService.getAll(branch)
      .then((response) => {
        if (response.status == 200) {
          let users;
          // Admin sees all users across all branches; when a specific branch is selected,
          // limit to users assigned to that branch.
          // Non-admin only sees users whose branches overlap with their own assigned branches.
          if (isAdmin()) {
            if (branch === 0) {
              users = response.data.filter((user) => user.isEnabled === true);
            } else {
              users = response.data.filter(
                (user) =>
                  user.isEnabled === true && Array.isArray(user.branches) && user.branches.some((b) => b.id == branch),
              );
            }
          } else {
            if (branch == 0) {
              const currentUserBranchIds = (currentUser?.branches || []).map((b) => b.id);
              users = response.data.filter(
                (user) =>
                  user.isEnabled === true &&
                  Array.isArray(user.branches) &&
                  user.branches.some((b) => currentUserBranchIds.includes(b.id)),
              );
            } else {
              users = response.data.filter(
                (user) =>
                  user.isEnabled === true && Array.isArray(user.branches) && user.branches.some((b) => b.id == branch),
              );
            }
          }

          for (let i = 0; i < users.length; i++) {
            const date = new Date(users[i].createdAt);
            users[i].formattedCreatedAt = moment(date).format("MMM D, YYYY");
          }
          setUserArray(users);
          loadFormPermissions(users.map((u) => u.id));
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
  };

  // Users without an explicit row are treated as "form disabled" (default OFF).
  const loadFormPermissions = (userIds) => {
    if (!userIds || userIds.length === 0) {
      setFormEnabledMap({});
      return;
    }
    UserFormPermissionService.getMany(userIds)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const map = {};
          userIds.forEach((id) => {
            map[id] = false;
          });
          (response.data || []).forEach((row) => {
            map[row.userId] = row.isFormEnabled;
          });
          setFormEnabledMap(map);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const toggleFormEnabled = (user, isFormEnabled) => {
    setFormEnabledMap((prev) => ({ ...prev, [user.id]: isFormEnabled }));
    UserFormPermissionService.set(user.id, isFormEnabled)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast(isFormEnabled ? `Form enabled for ${user.name}` : `Form disabled for ${user.name}`);
        }
      })
      .catch((error) => {
        setFormEnabledMap((prev) => ({ ...prev, [user.id]: !isFormEnabled }));
        handleError(error);
      });
  };

  const handleRoleChange = (user, role) => {
    if ((user.roles ?? []).some((r) => r.id === role.id)) {
      return;
    }
    persistUser(user, { roles: [role] }, "Role updated successfully");
  };

  const handleBranchChange = (user, branchOption, checked) => {
    const currentBranches = user.branches ?? [];
    const nextBranches = checked
      ? [...currentBranches, branchOption]
      : currentBranches.filter((b) => b.id !== branchOption.id);
    if (nextBranches.length === 0) {
      toast("User must belong to at least one branch");
      return;
    }
    persistUser(user, { branches: nextBranches }, "Branches updated successfully");
  };

  const persistUser = (user, fields, successMessage = "User updated successfully") => {
    setUserArray((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...fields } : u)));
    UserService.updateUserPartial(user.id, fields)
      .then((response) => {
        if (response.status == 201) {
          toast(successMessage);
          getAllUsers();
        }
      })
      .catch((error) => {
        handleError(error);
        getAllUsers();
      });
  };

  const deleteUser = () => {
    UserService.deleteUser(userArray[index].id)
      .then((response) => {
        if (response.status == 201) {
          toast("User archived successfully");
          getAllUsers();
        }
        setIsDeleteConfirmDialogShown(false);
      })
      .catch((error) => {
        setIsDeleteConfirmDialogShown(false);

        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
  };

  // const saveChanges = () => {
  //   console.log("roleChangedUserArray: ", roleChangedUserArray);
  //   UserService.updateUsers(roleChangedUserArray)
  //     .then((response) => {
  //       if (response.status == 201 && response.data == true) {
  //         toast("Saved successfully");
  //         navigate(-1);
  //       } else {
  //         toast("Something went wrong!");
  //       }
  //     })
  //     .catch((error) => {
  //       if (error.response) {
  //         toast(error.response.data.message);
  //       } else {
  //         toast("An error occured");
  //       }
  //     });
  // };

  const savePhone = (user) => {
    const trimmed = editingPhoneValue.trim();
    if (trimmed === (user.phone || "")) {
      setEditingPhoneUserId(null);
      return;
    }
    if (trimmed !== "" && !isValidUSPhone(trimmed)) {
      return;
    }
    const normalized = trimmed === "" ? "" : toE164Phone(trimmed);
    UserService.updateUserPartial(user.id, { phone: normalized })
      .then((response) => {
        if (response.status == 201) {
          toast("Phone number updated successfully");
          getAllUsers();
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setEditingPhoneUserId(null);
      });
  };

  return (
    <div>
      <ConfirmDialog
        title="ARCHIVE USER"
        description="Are you sure you want to archive this user?"
        shown={isDeleteConfirmDialogShown}
        onConfirmClicked={deleteUser}
        onCancelClicked={() => setIsDeleteConfirmDialogShown(false)}
      />
      <div
        className="w-full mt-3 overflow-hidden"
        style={{
          borderRadius: "12px",
          border: "1px solid #E5E8F3",
          background: "#FFF",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}>
              <th className="w-[70px]">
                <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                  No.
                </div>
              </th>
              <th>
                <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center gap-1 px-4 h-[44px] text-[#5F6477]">
                  <span>Name</span>
                  <span className="text-[10px]">↓</span>
                </div>
              </th>
              <th className="w-[140px]">
                <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                  Phone
                </div>
              </th>
              <th className="w-[200px]">
                <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                  Role
                </div>
              </th>
              <th className="w-[360px]">
                <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                  Branch
                </div>
              </th>
              {superAdmin && (
                <th className="w-[80px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center h-[44px] text-[#5F6477]">
                    Form
                  </div>
                </th>
              )}
              <th className="w-[160px]">
                <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center gap-1 px-4 h-[44px] text-[#5F6477]">
                  <span>Added</span>
                  <span className="text-[10px]">↓</span>
                </div>
              </th>
              <th className="w-[48px]"></th>
            </tr>
          </thead>
          <tbody>
            {userArray.map((user, i) => (
              <tr key={i} className="border-b border-borderColor hover:bg-[#FAFAFB]">
                <td>
                  <div className="font-OpenSansRegular px-4 py-3 text-[12px] font-[400] text-[#717680]">{i + 1}</div>
                </td>
                <td>
                  <div className="px-4 py-3 flex flex-col">
                    <span className="font-OpenSansRegular text-[14px] font-[600] leading-[20px] text-[#1A2332]">
                      {user.name}
                    </span>
                    <span className="font-OpenSansRegular text-[14px] font-[400] leading-[20px] text-[#5F6477] truncate max-w-[300px]">
                      {user.email}
                    </span>
                  </div>
                </td>
                <td className="relative">
                  {editingPhoneUserId === user.id ? (
                    <div className="flex items-center">
                      <input
                        autoFocus
                        className={`font-OpenSansRegular px-4 py-3 text-[12px] font-[400] w-full outline-none bg-transparent border-b ${editingPhoneValue && !isValidUSPhone(editingPhoneValue) ? "border-red-500" : "border-blue-500"}`}
                        value={editingPhoneValue}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                        onChange={(e) => setEditingPhoneValue(formatUSPhone(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") savePhone(user);
                          if (e.key === "Escape") setEditingPhoneUserId(null);
                        }}
                      />
                      <EditButton clicked={() => savePhone(user)} isEditing={true} />
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <div className="font-OpenSansRegular px-4 py-3 text-[14px] font-[400] leading-[20px] text-[#5F6477]">
                        {user.phone ? formatUSPhone(user.phone) : "—"}
                      </div>
                      {superAdmin && (
                        <EditButton
                          clicked={() => {
                            setEditingPhoneUserId(user.id);
                            setEditingPhoneValue(formatUSPhone(user.phone || ""));
                          }}
                        />
                      )}
                    </div>
                  )}
                </td>
                <td className="relative">
                  <div className="px-2 py-3">
                    {isAdmin() ? (
                      <RoleDropDown user={user} roles={roleArray} onChange={handleRoleChange} />
                    ) : (
                      <div className="flex flex-wrap items-center gap-1">
                        {(user.roles ?? []).slice(0, 2).map((role) => (
                          <span
                            key={role.id}
                            className="flex justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-center text-[#0058E5] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px] capitalize"
                          >
                            {role.name}
                          </span>
                        ))}
                        {(user.roles ?? []).length > 2 && (
                          <span className="font-OpenSansRegular text-[12px] text-[#535862] bg-[#F3F4F6] rounded-md px-2 py-1">
                            +{user.roles.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="relative">
                  <div className="px-2 py-3">
                    {isAdmin() ? (
                      <BranchDropDown user={user} branches={branchArray} onChange={handleBranchChange} />
                    ) : (
                      <div className="flex flex-wrap items-center gap-1">
                        {(user.branches ?? []).slice(0, 2).map((branch) => (
                          <span
                            key={branch.id}
                            className="flex flex-col justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px] capitalize"
                          >
                            {branch.name}
                          </span>
                        ))}
                        {(user.branches ?? []).length > 2 && (
                          <span className="flex flex-col justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px]">
                            +{user.branches.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                {superAdmin && (
                  <td>
                    <div className="flex items-center justify-center px-4 py-3">
                      <Toggle
                        controlled
                        leftBackgroundColor="#E6E7EC"
                        rightBackgroundColor="#0065FD"
                        borderColor="none"
                        knobColor={formEnabledMap[user.id] ? "white" : "#0065FD"}
                        name={`form-toggle-${user.id}`}
                        checked={!!formEnabledMap[user.id]}
                        onToggle={(e) => toggleFormEnabled(user, e.target.checked)}
                      />
                    </div>
                  </td>
                )}
                <td>
                  <div className="px-4 py-3 font-OpenSans text-[14px] font-normal leading-[20px] text-[#5F6477]">
                    {user.formattedCreatedAt}
                  </div>
                </td>
                <td>
                  {isAdmin() && (
                    <RowKebabMenu
                      onArchive={() => {
                        setIsDeleteConfirmDialogShown(true);
                        setIndex(i);
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Manage;

const RowKebabMenu = ({ onArchive }) => {
  const [open, setOpen] = useState(false);
  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className="relative flex items-center justify-center">
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer"
          aria-label="Row actions"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="3" r="1.4" fill="#717680" />
            <circle cx="8" cy="8" r="1.4" fill="#717680" />
            <circle cx="8" cy="13" r="1.4" fill="#717680" />
          </svg>
        </button>
        {open && (
          <div className="absolute right-2 top-8 z-20 min-w-[140px] bg-white border border-borderColor rounded shadow-md py-1">
            <div
              onClick={() => {
                setOpen(false);
                onArchive();
              }}
              className="font-OpenSansRegular text-[12px] text-[#181D27] px-3 py-2 cursor-pointer hover:bg-gray-50"
            >
              Archive user
            </div>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  return isEditing ? (
    <div onClick={clicked} className="w-[18px] absolute right-[5%] cursor-pointer">
      <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
    </div>
  ) : (
    <div
      onClick={isDisabled ? undefined : clicked}
      className={`w-[18px] absolute right-[5%] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
    </div>
  );
};
